import { useCallback, useEffect, useRef, useState } from "react";
import { UserDetails} from "./models";
import { useLoader } from "src/contexts/LoaderContext";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Button } from "../../../components/ui/button";
import { Input } from "src/components/ui/input";
import { Upload, Pencil, Trash2, User, UploadIcon } from "lucide-react";
import useApi from "../../../configs/api"
import { Dialog, DialogTrigger } from "src/components/ui/dialog";
import { UpdateAvatarDialog } from "./update-avatar-dialog";
import { PersonalInfoDialog } from "./personal-info-dialog";
import { PreferencesDialog } from "./preferences-dialog";
import { classNames } from "src/helpers/global";
import { AvatarSection } from "./AvatarSection";

const Profile = () => {
    const { showLoader, hideLoader } = useLoader();
    const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [preferencesOpen, setPreferencesOpen] = useState(false);
    const [openPersonaInfo, setOpenPersonaInfo] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [validPhoto, setValidPhoto] = useState<boolean>(true);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [removePhoto, setRemovePhoto] = useState<boolean>(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [updatingPhoto, setUpdatingPhoto] = useState(false);
    

    const api = useApi();

    const fetchUserDetails = useCallback(async() => {
        try {
            const res = await api.get("/user/me");
            if (res.status === 200)
            {
                const data = res.data.data;
                setUserDetails(data)
                
                if (data.avatar?.url) // if avatar url exists, set it as preview
                    setPreview(data.avatar.url);
            
            }
        }
        catch (error) {console.error("Failed to fetch user details:", error);}
    }, [api]);

    useEffect(() => {
        void fetchUserDetails();

        // cleanup memory when preview changes or component unmounts
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };

    }, [preview]);

    const handleProfileChanges = async () => { 
        try {
            await fetchUserDetails();
        } finally {
            hideLoader();
        }
    }

const handleUpdatePhotoChanges = (e : React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        setValidPhoto(false);

        if (!selected) return;

        if (!["image/jpeg", "image/png", "image/webp"].includes(selected.type)) {
            setValidPhoto(true);
            e.target.value = "";
            return;
        }

        if (selected.size > 5 * 1024 * 1024) {
            setValidPhoto(true);
            e.target.value = "";
            return;
        }

        setSelectedFile(selected);
        setRemovePhoto(false);
    }

    return (
        <div className="text-sm ">
        <div className="flex flex-col p-2 md:p-3 gap-3 ">            
                        
            <div className=" ">
                <p className="text-base font-semibold leading-7 text-teal-600 uppercase">Account</p>
                <p className="mt-2 text-2xl font-bold tracking-tight text-gray-900">Profile Settings</p>
                <span className="font-light text-slate-400">Manage your personal details and profile photo</span>

            </div>
            
            <hr className="h-px my-5 bg-gray-200 border-0"/>

            <div className="p-2 rounded-md flex flex-col lg:flex-row items-center">
              <AvatarSection/>                
            </div>

            <div className="p-5 rounded-md border border-gray-200">
                <div className="border-b border-gray-200 py-2">
                    <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
                        <div className="ml-4 mt-2">
                        <h3 className="text-base font-semibold leading-6 text-gray-900">Personal Information</h3>
                        </div>
                        <div className="ml-4 mt-2 flex-shrink-0">
                            <Dialog open={openPersonaInfo} onOpenChange={setOpenPersonaInfo}>
                                <DialogTrigger asChild>

                                <button type="button"
                                className="relative inline-flex items-center lg:border-b rounder-xl border-gray-300 py-1 px-3" title="Edit Personal Infromation"
                                >                            
                                    <Pencil className="size-4 md:size-3" />
                                    <span className="hidden md:inline ml-1">Edit</span>
                                </button>
                                </DialogTrigger>

                                <PersonalInfoDialog 
                                    data={userDetails?.personalInfo} 
                                    onClose={() => setOpenPersonaInfo(false)}
                                    onSubmit={handleProfileChanges}
                                />

                            </Dialog>
                        
                        </div>
                    </div>
                </div>
            
                <div className="grid grid-cols-2 gap-5 py-3">
                    <div className="col-span-2 md:col-span-1 flex-col lg:flex-row-flex lg:gap-5">
                        <label className="text-sm/6 font-semibold text-gray-900 dark:text-gray-10">First Name</label>
                        <p>{userDetails?.personalInfo.firstName}</p>
                    </div>
                    <div className="col-span-2 md:col-span-1 flex-col lg:flex-row-flex lg:gap-5">
                        <label className="text-sm/6 font-semibold text-gray-900 dark:text-gray-10">Last Name</label>
                        <p>{userDetails?.personalInfo.lastName}</p>
                    </div>
                    <div className="col-span-2 flex-col lg:flex-row-flex lg:gap-5">
                        <label className="text-sm/6 font-semibold text-gray-900 dark:text-gray-10">Date of Birth</label>
                        <p>
                        {userDetails?.personalInfo.dob
                            ? new Date(userDetails.personalInfo.dob).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })
                            : "-"}
                        </p>
                    </div>
                    <div className="col-span-2 flex-col lg:flex-row-flex lg:gap-5">
                        <label className="text-sm/6 font-semibold text-gray-900 dark:text-gray-10">Gender</label>
                        <p>{userDetails?.personalInfo.gender?.label || "-"}</p>
                    </div>
                </div>
            </div>

            <div className="p-5 rounded-md border border-gray-200">
                <div className="border-b border-gray-200 py-2 ">
                    <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
                        <div className="ml-4 mt-2">
                        <h3 className="text-base font-semibold leading-6 text-gray-900">Account</h3>
                        </div>
                        <div className="ml-4 mt-2 flex-shrink-0">
                        <button
                            type="button"
                            className="relative inline-flex items-center md:border-b border-gray-300 py-1 px-3" title="Edit Personal Infromation"
                        >                            
                            <Pencil className="size-4 md:size-3" />
                            <span className="hidden md:inline ml-1">Edit</span>
                        </button>
                        </div>
                    </div>
                </div>
            
                <div className="grid grid-cols-2 gap-5 py-3">
                    <div className="col-span-2 md:col-span-1 flex-col lg:flex-row-flex lg:gap-5">
                        <label className="text-sm/6 font-semibold text-gray-900 dark:text-gray-10">Username</label>
                        <p>{userDetails?.account.username}</p>
                    </div>
                    <div className="col-span-2 md:col-span-1 flex-col lg:flex-row-flex lg:gap-5">
                        <label className="text-sm/6 font-semibold text-gray-900 dark:text-gray-10">Email Address</label>
                        <p>{userDetails?.account.email}</p>
                    </div>
                    
                </div>
            </div>

            <div className="p-5 rounded-md border border-gray-200">
                <div className="border-b border-gray-200 py-2 ">
                    <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
                        <div className="ml-4 mt-2">
                        <h3 className="text-base font-semibold leading-6 text-gray-900">Preferences</h3>
                        </div>
                        <div className="ml-4 mt-2 flex-shrink-0">
                            <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
                                <DialogTrigger asChild>

                                <button type="button"
                                
                                className="relative inline-flex items-center md:border-b border-gray-300 py-1 px-3" title="Edit Preferences"
                                >                            
                                    <Pencil className="size-4 md:size-3" />
                                    <span className="hidden md:inline ml-1">Edit</span>
                                </button>
                                </DialogTrigger>

                                <PreferencesDialog 
                                data={userDetails?.preferences}
                                onClose={() => setPreferencesOpen(false)}
                                onSubmit={handleProfileChanges}
                                                                    
                                />

                            </Dialog>
                        
                        </div>
                    </div>
                </div>
            
                <div className="grid grid-cols-2 gap-5 py-3">
                    <div className="col-span-2 md:col-span-1 flex-col lg:flex-row-flex lg:gap-5">
                        <label className="text-sm/6 font-semibold text-gray-900 dark:text-gray-10">Currency</label>
                        <p>{userDetails?.preferences.currency.label}</p>
                    </div>
                    
                </div>
            </div>
        </div>
        </div>
        
    )
}



export default Profile;